import { Divider, ScrollArea, Stack, Text } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import type { QueryObserverResult } from '@tanstack/react-query';
import { isArray } from 'lodash';
import { type ReactElement, type ReactNode, useCallback, useState } from 'react';
import type { ArrayPath, Control } from 'react-hook-form';
import { Show } from '../../../../../../react-commons/components/control-flow/Show';
import { CollapsibleSection } from '../../../../../../react-commons/components/data-display/CollapsibleSection';
import { Section } from '../../../../../../react-commons/components/data-display/Section';
import { Controlled_StringInput } from '../../../../../../react-commons/components/inputs/controlled/Controlled_StringInput';
import { LabelNode } from '../../../../../../react-commons/components/inputs/getLabelNode';
import { ValidationEndAdornment } from '../../../../../../react-commons/components/ValidationEndAdornment';
import { useField } from '../../../../../../react-commons/hooks/useField';
import type {
    ExtractAllEnumKeys,
    ExtractStringArrayKeys,
    ExtractStringKeys,
} from '../../../../../../react-commons/types/extractKeysOfType';
import { formatIdentifier, isFunction } from '../../../../../../react-commons/utils/utilFunctions';
import type { PersistableResource } from '../../../../resource/resource-base/persistableResourceTypes';
import type { PropertyDetailsResource } from '../../../../resource/resources/propertyDetailsResource';
import type { PropertyValidation } from '../../../../resource/services/outlineTypes';
import { getPropertyLabelInfo } from '../../../util/utilFunctions';
import { Controlled_AutocompleteReferenceTextField } from './Controlled_AutocompleteReferenceTextField';

export const Form_PropertyFieldArrayReference = <P extends PersistableResource, T extends object>({
    name,
    control,
    readOnly,
    disabled,
    propertyDetails,
    propertyValidation,
    initialOpen,
    label,
    handleExpansionChange,
    onGoToClick,
    toolTipValue,
    onEdit,
    idSelector,
    labelAccessor,
    query,
    typeKey,
    withoutDuplicates,
    withSearch,
    maxAmount,
}: {
    name: ExtractStringArrayKeys<T, false>;
    control: Control<T>;
    initialOpen?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    label?: ReactNode;
    propertyDetails: PropertyDetailsResource | Array<PropertyDetailsResource> | undefined;
    propertyValidation: PropertyValidation | null | undefined;
    handleExpansionChange?: (value: boolean) => void;
    idSelector: keyof P & ('id' | 'objectId');
    query: QueryObserverResult<P | Array<P> | null>;
    onGoToClick?: (resourceId: string | undefined, openInNewTab: boolean) => void;
    labelAccessor: (keyof P & ExtractStringKeys<P, false>) | ((resource: P) => string);
    toolTipValue?: (resource: P) => ReactElement | undefined;
    onEdit?: (id: string) => void;
    typeKey?: keyof P & ExtractAllEnumKeys<P>;
    uniqueSelection?: boolean; // TODO use or remove
    withoutDuplicates?: boolean;
    withSearch?: boolean;
    maxAmount?: number;
}) => {
    const getOptionLabel = useCallback(
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        (r: P) => (isFunction(labelAccessor) ? labelAccessor(r) : (r[labelAccessor] as string)),
        [labelAccessor]
    );

    const { value: fields, onChange } = useField<Array<string | null>, T>({
        control,
        name: name as ArrayPath<T>,
    });

    const prepend = useCallback(() => onChange([null, ...fields]), [fields, onChange]);
    const remove = useCallback((index: number) => onChange(fields.toSpliced(index, 1)), [fields, onChange]);
    const update = useCallback(
        (index: number, newValue: string | null) => onChange(fields.toSpliced(index, 1, newValue)),
        [fields, onChange]
    );

    const [searchText, setSearchText] = useState<string | null>(null);

    const severity = propertyValidation?.severity;
    const helperText = propertyValidation?.identifier;

    const filter = (resources: Array<P>, selectedResource: P | null) => {
        if (withoutDuplicates) {
            return resources.filter(
                (resource) => !fields.includes(resource.id) || resource.id === selectedResource?.id
            );
        }
        return resources;
    };

    const getPropertyTableTitle = () => {
        if (propertyDetails) {
            const text =
                isArray(propertyDetails) ?
                    (propertyDetails[0]?.displayName ?? formatIdentifier(propertyDetails[0]?.propertyName))
                : propertyDetails ? (propertyDetails.displayName ?? formatIdentifier(propertyDetails.propertyName))
                : label;
            const typography = (
                <Text style={{ lineHeight: 1.8 }} size={'sm'} c="dimmed">
                    {text}
                </Text>
            );
            return <LabelNode label={typography} labelInfo={getPropertyLabelInfo(propertyDetails)} />;
        }
        return label;
    };

    if (fields.length === 0) {
        return (
            <Section
                label={getPropertyTableTitle()}
                handleAddClick={!readOnly ? prepend : undefined}
                rightLabelBarNode={
                    <ValidationEndAdornment severity={severity} helperText={helperText ? [helperText] : undefined} />
                }
            />
        );
    }

    return (
        <CollapsibleSection
            withoutBackgroundColor={true}
            label={getPropertyTableTitle()}
            labelBarNode={
                <ValidationEndAdornment severity={severity} helperText={helperText ? [helperText] : undefined} />
            }
            handleAddClick={prepend}
            initialOpen={initialOpen}
            disabled={disabled}
            disableAdd={fields.includes(null) || Boolean(maxAmount && fields.length >= maxAmount)}
            handleExpansionChange={handleExpansionChange}
        >
            <Show when={withSearch}>
                <Controlled_StringInput
                    nullable={true}
                    readOnly={false}
                    placeholder={'Search'}
                    size={'xs'}
                    pb={4}
                    value={searchText}
                    onChange={setSearchText}
                    endingIcon={<IconSearch />}
                />
                <Divider pb={4} />
            </Show>
            <ScrollArea.Autosize mah={300} scrollbars={'y'} offsetScrollbars={'y'}>
                <Stack>
                    {fields
                        .filter((id) => {
                            if (!withSearch || !searchText) {
                                return true;
                            }
                            const resource = query.data?.let((it) =>
                                isArray(it) ? it.find((r) => r[idSelector] === id) : it
                            );
                            if (resource) {
                                const name = getOptionLabel(resource);
                                return name.includes(searchText);
                            }
                            return true;
                        })
                        .map((field) => {
                            const index = fields.indexOf(field);
                            return (
                                <Controlled_AutocompleteReferenceTextField
                                    key={index}
                                    idValue={field}
                                    onChange={(newValue) => update(index, newValue)}
                                    query={query}
                                    groupBy={typeKey && ((r) => r[typeKey])}
                                    getOptionLabel={getOptionLabel}
                                    onGoToClick={onGoToClick}
                                    propertyDetails={undefined}
                                    propertyValidation={undefined}
                                    getToolTip={(resource) =>
                                        toolTipValue && resource ? toolTipValue(resource) : undefined
                                    }
                                    hideLabel={true}
                                    filterResources={filter}
                                    onDelete={() => remove(index)}
                                    onEdit={onEdit ? () => onEdit(field ?? '') : undefined}
                                    readOnly={readOnly}
                                    idSelector={idSelector}
                                    notNullable={true}
                                />
                            );
                        })}
                </Stack>
            </ScrollArea.Autosize>
        </CollapsibleSection>
    );
};
